/**
 * La barra de arriba de los asistentes de pantalla completa.
 *
 * Nació en «Página nueva» y se quedó allí: los demás asistentes —oferta,
 * servicio, cita— llevaban una barra propia con el título a la izquierda y la
 * «X» al final, que resolvía lo mismo con otra forma. Con dos barras distintas,
 * entrar en un asistente u otro cambiaba de sitio la salida y hacía desaparecer
 * la marca, y eso se lee como cambiar de aplicación, no de tarea.
 *
 * Lleva dos cosas, y ninguna es decorativa:
 *
 *   • La marca, que además es la salida hacia atrás. Es el ancla: en una
 *     pantalla que tapa el panel entero, es lo único que dice dónde estás. Sin
 *     el «Dashboard ⌄» de dentro de un proyecto: un asistente es una tarea con
 *     su salida, no un sitio desde el que saltar a otra área.
 *   • El nombre de lo que se está creando, editable ahí mismo. Preguntarlo en
 *     la barra y también dentro de un paso lo pregunta dos veces; quien no
 *     tiene nombre que editar —una cita no se llama de ninguna manera— enseña
 *     el título del asistente en ese hueco, con `title`.
 */
import type { ReactNode } from 'react'
import { IconPencil, IconX } from '@tabler/icons-react'
import { BrandLockup } from '../BrandLockup'
import { useI18n } from '../../lib/i18n'

/** El campo del nombre, cuando lo que se crea tiene uno. */
export type WizardTopBarName = {
  value: string
  onChange: (value: string) => void
  /** Rótulo para quien no ve el campo. Se usa de marcador si no hay otro. */
  aria: string
  placeholder?: string
  autoFocus?: boolean
  maxLength?: number
}

export function WizardTopBar({
  onBack,
  name,
  title,
  center,
  progress,
  actions,
  onClose,
  closeHidden = false,
  busy = false,
}: {
  /** La marca, que es el botón de volver. */
  onBack: () => void
  name?: WizardTopBarName
  /** El título fijo, para lo que no tiene nombre editable. */
  title?: string
  /**
   * El raíl de pasos, en medio de la barra.
   *
   * En medio y no debajo: en un asistente de pantalla completa, la fila de
   * pasos y la barra eran dos franjas seguidas de la misma altura y el mismo
   * fondo, o sea 120px de cabecera antes de la primera pregunta. En cuanto la
   * ventana se estrecha baja a su propio renglón, porque al lado de la marca y
   * de la salida no cabe sin encogerse hasta no poder leerse — eso lo hace el
   * CSS, aquí sólo se pinta una vez.
   */
  center?: ReactNode
  /** «Paso 2 de 4» y parecidos. */
  progress?: ReactNode
  /** Acciones propias antes de la «X» — guardar, cancelar. */
  actions?: ReactNode
  onClose: () => void
  /**
   * Esconde la «X».
   *
   * Con cambios sin guardar y dos acciones al lado, una «X» es un botón que
   * tira el trabajo de un clic y sin preguntar. Quien tenga ese caso pone sus
   * propias salidas en `actions` y apaga ésta.
   */
  closeHidden?: boolean
  /** Mientras se graba no se sale ni se vuelve. */
  busy?: boolean
}) {
  const { t } = useI18n()

  return (
    <header className="dash-create-header">
      <div className="dash-create-brand">
        <div className="dash-brand-cluster is-fit">
          <button className="dash-topbar-home" type="button" aria-label={t('newPage.backToDash')} disabled={busy} onClick={onBack}>
            <BrandLockup mascotClassName="dash-topbar-logo dash-topbar-logo--mark" mascotSize={23} />
          </button>
        </div>
        {/* El filo sólo cuando hay algo que separar. Sin nombre ni título
            —los asistentes cuyo raíl ya dice dónde estás— era una raya
            colgando al lado de la marca, separando la marca de nada. */}
        {name || title ? <span className="dash-create-brand-divider" aria-hidden="true" /> : null}
        {name ? (
          <label className="dash-create-project-name">
            <input
              aria-label={name.aria}
              value={name.value}
              onChange={(event) => name.onChange(event.target.value)}
              autoFocus={name.autoFocus ?? false}
              placeholder={name.placeholder ?? name.aria}
              maxLength={name.maxLength ?? 80}
            />
            <IconPencil size={14} aria-hidden="true" />
          </label>
        ) : title ? (
          <strong className="dash-create-static-name">{title}</strong>
        ) : null}
      </div>
      {center ? <div className="dash-create-header-center">{center}</div> : null}
      <div className="dash-create-header-actions">
        {progress ? <span className="dash-create-progress">{progress}</span> : null}
        {actions}
        {closeHidden ? null : (
          <button className="dash-create-close" type="button" aria-label={t('common.close')} disabled={busy} onClick={onClose}>
            <IconX size={18} />
          </button>
        )}
      </div>
    </header>
  )
}
